import React, { useEffect, useRef, useState } from "react";
import { LogIn, ShieldAlert } from "lucide-react";

// Mounted once in App.js. Session checks call showSessionExpiredPopup(message)
// instead of alert(). Several pages run the check on the same load, so calls
// made while the popup is already open are ignored: the user sees it once.
const listeners = new Set();
let pendingMessage = null;

const DEFAULT_MESSAGE = "Your session has expired. Please login again.";

export const showSessionExpiredPopup = (message) => {
  const text = message || DEFAULT_MESSAGE;
  if (listeners.size === 0) {
    pendingMessage = text;
    return;
  }
  listeners.forEach((listener) => listener(text));
};

const SessionExpiredPopup = () => {
  const [message, setMessage] = useState(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const listener = (text) => setMessage((current) => current || text);
    listeners.add(listener);
    if (pendingMessage) {
      listener(pendingMessage);
      pendingMessage = null;
    }
    return () => listeners.delete(listener);
  }, []);

  const isOpen = Boolean(message);

  useEffect(() => {
    if (!isOpen) return undefined;

    buttonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setMessage(null);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // The backend sends a bare "Session expired" most of the time; only show its
  // text as the description when it says something more specific.
  const isGeneric = /^session expired\.?$/i.test(message.trim());
  const description = isGeneric ? DEFAULT_MESSAGE : message;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in-0 duration-200"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-message"
    >
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-300 motion-reduce:animate-none">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#503691] via-purple-600 to-rose-400" />

        <div className="px-6 pb-6 pt-8 text-center sm:px-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50 text-rose-500 ring-8 ring-rose-50/60">
            <ShieldAlert size={30} strokeWidth={2.2} />
          </div>

          <h2 id="session-expired-title" className="mt-5 text-xl font-bold text-gray-900">
            Session expired
          </h2>
          <p id="session-expired-message" className="mt-2 text-sm leading-relaxed text-gray-600">
            {description}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            For your security, you've been logged out.
          </p>

          <button
            ref={buttonRef}
            type="button"
            onClick={() => setMessage(null)}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#503691] to-purple-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-[#503691]/25 transition-all hover:from-[#3f2d7a] hover:to-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503691] focus-visible:ring-offset-2"
          >
            <LogIn size={18} />
            Login again
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredPopup;
